import "./ColumnSelector.scss";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FaMapMarkerAlt } from 'react-icons/fa';
import { Paths } from "../../paths";

export function useColumnSelector(storageKey, defaultColumns) {
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) setSelectedColumns(JSON.parse(saved));
  }, [storageKey]);

  const toggleColumn = (colKey) => {
    const updated = selectedColumns.includes(colKey)
      ? selectedColumns.filter((c) => c !== colKey)
      : [...selectedColumns, colKey];

    setSelectedColumns(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  return { selectedColumns, toggleColumn };
}

function getWikiThumb(url?: string | null, width = 100) {
  if (!url) return "";
  return url.replace(/\/\d+px-/, `/${width}px-`);
}

export type Column = {
    key: string;
    label: string;
    toggleColumn: boolean;
    forMobile: boolean;
    isDefault: boolean;
    render?: (row: any, navigate: any) => React.ReactNode;
  };

export const allColumns: Column[] = [
    { key: "name", label: "Vineyard Name", toggleColumn: false, forMobile: true, isDefault: true,
        render: (row, navigate) => (
        <a
            href={Paths.vineyard.replace(':id', row.composite)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
            if (e.metaKey || e.ctrlKey) return;
            e.preventDefault();
            navigate(Paths.vineyard.replace(':id', row.composite));
            }}
        >
            <span className="search-link">{row.name}</span>
        </a>
        ),
    },
    { key: "photo", label: "Photo", toggleColumn: true, forMobile: true, isDefault: false,
        render: (row) => (<>{!(row.photoUrl == null || row.photoUrl === "") ? <img src={getWikiThumb(row.preferredImage,100)} alt="" loading="lazy"/>: <></>}</>) 
    },
    { key: "ownerOrProducerName", label: "Producer / Owner", toggleColumn: true, forMobile: true, isDefault: false },
    { key: "country", label: "Country", toggleColumn: true, forMobile: true, isDefault: true },
    { key: "region", label: "Region", toggleColumn: true, forMobile: true, isDefault: true },
    { key: "subRegion", label: "Subregion", toggleColumn: true, forMobile: true, isDefault: true },
    { key: "subSubRegion", label: "Sub-Subregion", toggleColumn: true, forMobile: true, isDefault: true },
    { key: "varieties", label: "Varieties", toggleColumn: true, forMobile: true, isDefault: false },
    { key: "yearPlanted", label: "Date Planted", toggleColumn: true, forMobile: true, isDefault: true },
    { key: "ageOfVines", label: "Age", toggleColumn: true, forMobile: true, isDefault: true },
    { key: "sizeOfPlantingInHa", label: "ha", toggleColumn: true, forMobile: true, isDefault: false,
        render: (row) => (
            (row.isEstimated ? "~" : "") + (row.sizeOfPlantingInHa ?? "")
        ),
        },
    { key: "elevationInMeters", label: "Elevation", toggleColumn: true, forMobile: true, isDefault: false },
    { key: "location", label: "", toggleColumn: true, forMobile: true, isDefault: true,
        render: (row) => (
            <>{!(row.latitude === "" || row.latitude === null) ? <FaMapMarkerAlt color="#888"/> : <></>}</>
        )
    }
    ];

interface ColumnSelectorProps {
  allColumns: Column[];
  selectedColumns: string[];
  toggleColumn: (colKey: string) => void;
}

export function ColumnSelector({ allColumns, selectedColumns, toggleColumn }: ColumnSelectorProps) {
  // ✅ Hooks always at the top
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{top: number; left: number}>({top: 0, left: 0});
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update popup position when open
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + window.scrollY, left: rect.right + window.scrollX - 260 });
    }
  };

  useEffect(() => {
    if (open) updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [open]);

  // ✅ Always get portal after hooks
  const portalContainer = document.getElementById("column-selector-portal");
  if (!portalContainer) return null;

  const popup = (
    <div
      ref={popupRef}
      style={{ top: coords.top, left: coords.left, position: "absolute", width: 260 }}
      className="bg-white border shadow-xl rounded p-3 z-50"
    >
      {allColumns.map((col) => (
        col.toggleColumn && (
        <label key={col.key} className="description-text checkbox-text">
          <input
            type="checkbox"
            checked={selectedColumns.includes(col.key)}
            onChange={() => toggleColumn(col.key)}
            className="checkbox form-check-input"
          />
          {col.label === "" ? col.key : col.label}
        </label>)
      ))}
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((prev) => !prev)}
        className="px-3 py-2 border rounded bg-white shadow-sm description-text checkbox-text"
      >
        Select Columns
      </button>
      {open && createPortal(popup, portalContainer)}
    </>
  );
}
